// A station that keeps a different time from its city.
//
// Roma Street is on Sydney's North Coast line and Brisbane does not move its
// clocks, so in summer a train "due 04:53" by Sydney's clock reaches a platform
// where it is 03:53. Broken Hill is half an hour behind the rest of New South
// Wales all year. The bundle says which stations those are (`tz`); this is how a
// time is read there.
//
// THE TIMES THEMSELVES ARE RIGHT. GTFS files carry the AGENCY's clock whatever a
// stop says, and the server turns that into an instant, so nothing upstream is
// wrong. Checked against the real Brisbane timetable: the overnight XPT is
// filed as arriving Roma Street at 28:53 on Sydney's summer clock, and is
// published as 3:53am at Roma Street. The instant is the same; what changes is
// which wall it is read off.
//
// PURE, so a verdict about a clock can be tested without a browser.

/** The short name for a zone, as people say it. Null for one with no such name. */
const NAMES = {
  'Australia/Brisbane': 'QLD time',
  'Australia/Adelaide': 'SA time',
  // Broken Hill keeps South Australia's clock, and "SA time" is what the town
  // calls it.
  'Australia/Broken_Hill': 'SA time',
  'Australia/Darwin': 'NT time',
  'Australia/Perth': 'WA time',
  'Australia/Hobart': 'TAS time',
};

export function zoneName(tz) {
  return NAMES[tz] ?? null;
}

const FORMAT = { hour: '2-digit', minute: '2-digit', hour12: false };

/** HH:MM on one wall: the zone's when `tz` is given, else the device's. */
export function wallClock(value, tz = null) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleTimeString('en-AU', tz ? { ...FORMAT, timeZone: tz } : FORMAT);
  } catch {
    // A zone this runtime does not know. The device's clock is a wrong answer
    // for that station, and a missing one is no better: say it plainly.
    return d.toLocaleTimeString('en-AU', FORMAT);
  }
}

/**
 * "QLD time", when this station's wall differs from the viewer's at that
 * instant, else the empty string.
 *
 * COMPARED BY WHAT IS ON THE TWO CLOCKS rather than by zone name: Brisbane and
 * Sydney are different zones that agree for half the year, and in those months
 * a suffix on every time at Roma Street is noise the viewer has to read past.
 */
export function zoneNote(value, tz = null) {
  const name = tz ? zoneName(tz) : null;
  if (!name) return '';
  const there = wallClock(value, tz);
  if (!there) return '';
  return there === wallClock(value) ? '' : name;
}
